import { Box, Column, Row, Space } from "@stenajs-webui/core";
import { SpaceValues } from "@/lib/utils/SpaceValues";
import {
  AgreedVolumeSummary,
  ReportScreenCustomerFragment,
} from "@/gql/graphql";
import { useRef } from "react";
import StepContainer from "./StepContainer";
import { useGetContainerWidth } from "../hooks/getRowContainerWidth";
import { CustomerDetailsStep } from "./customer-details/CustomerDetailsStep";
import SpecificationsStep from "./specifications/SpecificationsStep";
import { AgreedVolumeSummaryView } from "./agreed-volume/AgreedVolumeSummary";

type CustomerDetailsWithSpecificationsStepProps = {
  customerSubscribers: ReportScreenCustomerFragment["subscriberContactsForReport"];
  customer: ReportScreenCustomerFragment | null | undefined;
  reportType: String;
  selectedCustomerSubscriberContact:
    | ReportScreenCustomerFragment["subscriberContactsForReport"]["0"]
    | undefined;
  setSelectedCustomerSubscriberContact: React.Dispatch<
    React.SetStateAction<
      | ReportScreenCustomerFragment["subscriberContactsForReport"]["0"]
      | undefined
    >
  >;
  customerId: string;
  year: number;
  month: number;
  headAgreementNumber?: number;
  modeOfPayment?: string;
  invoiceCurrency?: string;
  validFrom?: string;
  validTo?: string;
  includeCreditLimit: boolean;
  setIncludeCreditLimit: React.Dispatch<React.SetStateAction<boolean>>;
  includeThresholds: boolean;
  setIncludeThresholds: React.Dispatch<React.SetStateAction<boolean>>;
  specificationsText: string;
  setSpecificationsText: React.Dispatch<React.SetStateAction<string>>;
  setIsNextAvailable: React.Dispatch<React.SetStateAction<boolean>>;
  isCreatingReportForParent: boolean;
  agreedVolumeSummaries: AgreedVolumeSummary[];
};

export const CustomerDetailsWithSpecificationsStep = ({
  customerSubscribers,
  reportType,
  customer,
  selectedCustomerSubscriberContact,
  setSelectedCustomerSubscriberContact,
  setIsNextAvailable,
  customerId,
  year,
  month,
  isCreatingReportForParent,
  headAgreementNumber,
  modeOfPayment,
  invoiceCurrency,
  validFrom,
  validTo,
  includeCreditLimit,
  setIncludeCreditLimit,
  includeThresholds,
  setIncludeThresholds,
  specificationsText,
  setSpecificationsText,
  agreedVolumeSummaries,
}: CustomerDetailsWithSpecificationsStepProps) => {
  const rowContainer = useRef<HTMLDivElement>(null!);
  const { containerWidth } = useGetContainerWidth(rowContainer);

  const hasAgreedVolumeSummaries = agreedVolumeSummaries?.length > 0;

  return (
    <StepContainer>
      <Space num={SpaceValues.EIGHT} />
      <Row width={"100%"} flexWrap={"wrap"} ref={rowContainer}>
        <Column gap={SpaceValues.SIXTEEN}>
          {isCreatingReportForParent && (
            <Box
              width={containerWidth}
              minWidth={"1000px"}
              justifyContent={"space-between"}
            >
              <CustomerDetailsStep
                customer={customer}
                reportType={reportType}
                customerSubscribers={customerSubscribers}
                selectedCustomerSubscriberContact={
                  selectedCustomerSubscriberContact
                }
                setSelectedCustomerSubscriberContact={
                  setSelectedCustomerSubscriberContact
                }
              />
            </Box>
          )}
          {hasAgreedVolumeSummaries && (
            <Box
              width={containerWidth}
              minWidth="1000px"
              justifyContent="space-between"
            >
              <AgreedVolumeSummaryView
                agreedVolumeSummaries={agreedVolumeSummaries}
              />
            </Box>
          )}
        </Column>
        <Box
          width={
            isCreatingReportForParent || hasAgreedVolumeSummaries
              ? containerWidth
              : "100%"
          }
          minWidth={"1000px"}
        >
          <SpecificationsStep
            setIsNextAvailable={setIsNextAvailable}
            customerId={customerId}
            year={year}
            month={month}
            isCreatingReportForParent={isCreatingReportForParent}
            headAgreementNumber={headAgreementNumber}
            modeOfPaymemt={modeOfPayment}
            invoiceCurrency={invoiceCurrency}
            validFrom={validFrom}
            validTo={validTo}
            includeCreditLimit={includeCreditLimit}
            setIncludeCreditLimit={setIncludeCreditLimit}
            includeThresholds={includeThresholds}
            setIncludeThresholds={setIncludeThresholds}
            specificationsText={specificationsText}
            setSpecificationsText={setSpecificationsText}
          />
        </Box>
        <Space num={SpaceValues.SIXTEEN} />
      </Row>
      <Space num={SpaceValues.TWENTYFOUR} />
    </StepContainer>
  );
};
